import apiClient from './api';

// Product service to handle product-related API calls
class ProductService {
  // Get all products with optional filters
  static async getProducts(filters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.category) params.append('category', filters.category);
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.search) params.append('search', filters.search);
      if (filters.listing_type) params.append('listing_type', filters.listing_type);
      if (filters.option_value) params.append('option_value', filters.option_value);
      if (filters.is_featured !== undefined) params.append('is_featured', filters.is_featured);
      if (filters.is_trending !== undefined) params.append('is_trending', filters.is_trending);
      if (filters.is_top_selling !== undefined) params.append('is_top_selling', filters.is_top_selling);
      if (filters.min_price !== undefined) params.append('min_price', filters.min_price);
      if (filters.max_price !== undefined) params.append('max_price', filters.max_price);
      if (filters.skip !== undefined) params.append('skip', filters.skip);
      if (filters.limit !== undefined) params.append('limit', filters.limit);
      if (filters.sort_by) params.append('sort_by', filters.sort_by);
      if (filters.sort_order) params.append('sort_order', filters.sort_order);
      // Optional cache-buster (useful for admin screens because backend caches /products for 60s)
      if (filters._t !== undefined) params.append('_t', filters._t);
      
      const queryString = params.toString();
      const url = queryString ? `/products/?${queryString}` : '/products/';

      // Avoid stale browser/CDN caches returning an old empty list after the DB is seeded.
      const response = await apiClient.get(url, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      return { success: true, products: response.data };
    } catch (error) {
      console.error('Get products error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to fetch products' 
      };
    }
  }

  // Get featured products
  static async getFeaturedProducts(extra = {}) {
    return this.getProducts({ is_featured: true, ...extra });
  }

  // Get trending products
  static async getTrendingProducts(extra = {}) {
    return this.getProducts({ is_trending: true, ...extra });
  }

  // Get top selling products
  static async getTopSellingProducts(extra = {}) {
    return this.getProducts({ is_top_selling: true, ...extra });
  }

  // Get single product by ID
  static async getProduct(productId) {
    try {
      const response = await apiClient.get(`/products/${productId}`, {
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      return { success: true, product: response.data };
    } catch (error) {
      console.error('Get product error:', error);
      // 503 = the product is fine but its store is temporarily hidden
      // (suspended/non-payment) -- show a friendly message instead of a
      // generic "not found" redirect (D14 visibility gate).
      return {
        success: false,
        unavailable: error.response?.status === 503,
        error: error.response?.data?.detail || 'Failed to fetch product',
      };
    }
  }

  // Get products by category
  static async getProductsByCategory(category) {
    return this.getProducts({ category });
  }

  // Search products
  static async searchProducts(searchTerm) {
    return this.getProducts({ search: searchTerm });
  }

  // Create new product (admin only)
  static async createProduct(productData) {
    try {
      const response = await apiClient.post('/products/', productData);
      return { success: true, product: response.data };
    } catch (error) {
      console.error('Create product error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to create product' 
      };
    }
  }

  // Update product (admin only)
  static async updateProduct(productId, productData) {
    try {
      const response = await apiClient.put(`/products/${productId}`, productData);
      return { success: true, product: response.data };
    } catch (error) {
      console.error('Update product error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to update product' 
      };
    }
  }

  // Delete product (admin only)
  static async deleteProduct(productId) {
    try {
      await apiClient.delete(`/products/${productId}`);
      return { success: true };
    } catch (error) {
      console.error('Delete product error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to delete product' 
      };
    }
  }

  // Get product categories
  static async getCategories() {
    try {
      const productsResult = await this.getProducts();
      if (productsResult.success) {
        const items = productsResult.products?.items || [];
        const categories = [...new Set(
          items
            .map(product => product.category)
            .filter(category => category)
        )];
        return { success: true, categories };
      }
      return { success: false, error: 'Failed to fetch categories' };
    } catch (error) {
      console.error('Get categories error:', error);
      return { 
        success: false, 
        error: 'Failed to fetch categories' 
      };
    }
  }

  // Get product brands
  static async getBrands() {
    try {
      const productsResult = await this.getProducts();
      if (productsResult.success) {
        const items = productsResult.products?.items || [];
        const brands = [...new Set(
          items
            .map(product => product.brand)
            .filter(brand => brand)
        )];
        return { success: true, brands };
      }
      return { success: false, error: 'Failed to fetch brands' };
    } catch (error) {
      console.error('Get brands error:', error);
      return { 
        success: false, 
        error: 'Failed to fetch brands' 
      };
    }
  }
}

export default ProductService;